import {
  ECartTypeId,
  ECurrency,
  ELanguage,
  ESalutation,
} from '@ringana/configs';
import {
  IApiAddCartItemRequestBody,
  IApiCoupon,
  IApiCustomerDataSheet,
  IApiDataSheetOrder,
  IApiDatasheetOrdersFilter,
  IApiNewsletterFormData,
  IApiPartnerInfo,
  IApiSearchResult,
} from '@/common/interfaces/api.interface';
import { IBlogPostsData } from '@/common/interfaces/blog.interface';
import { IChannelizeBroadcast } from '@/common/interfaces/channelize.interface';
import { ICustomerSession } from '@/common/interfaces/customers.interface';
import { IEvent } from '@/common/interfaces/events.interface';
import { INewsletterFormData } from '@/common/interfaces/frontpage.interface';
import { IImage } from '@/common/interfaces/image.interface';
import { ICurrency } from '@/common/interfaces/index.interface';
import { IUpdateAddress } from '@/common/interfaces/my-profile.interface';
import {
  IDocuSignRecipientViewData,
  IFrenchPartnerApplicationDocumentOptions,
  IGenericPartnerApplicationDocumentOptions,
  IItalianPartnerApplicationDocumentOptions,
  IPartnerApplication,
  IPartnerApplicationResponse,
  IPartnerApplicationSendPADto,
} from '@/common/interfaces/partner-application.interface';
import {
  IMasterSummitDashboardData,
  IPartnerSearchResult,
  ISpecialSummitDashboardData,
  IUplineData,
} from '@/common/interfaces/partners.interface';
import {
  IAddress,
  IAddressData,
  IVoucherAssignmentCustomerData,
} from '@/common/interfaces/shared.interface';
import {
  IIncentive,
  IIncentiveRanking,
  IIncentivesData,
  ISearchResults,
  IVotePartner,
} from '@/common/interfaces/single-incentive.interface';
import AnnouncementBarHelper from '@/helpers/AnnouncementBarHelper';
import EventsHelper from '@/helpers/page-specific/EventsHelper';
import ZApiRoutes from '../api/ZApiRoutes';
import { IApiBarometerData } from '../common/interfaces/barometer.interface';
import { ICalendarData } from '../common/interfaces/calendar.interface';
import { ICreateShoppingCart, IShoppingCart } from '../common/interfaces/cart.interface';
import { ICheckoutTaxNumberData } from '../common/interfaces/checkout.interface';
import { IBouncerConfig, ICountry, ISalutation } from '../common/interfaces/config.interface';
import { IDashboardData } from '../common/interfaces/dashboard.interface';
import { ICoordinates, IMapParams } from '../common/interfaces/location.interface';
import { IProduct } from '../common/interfaces/products.interface';
import { IGetCalendarRequestData, IPatchCartItemRequestData } from '../common/interfaces/request.interface';
import { TApiResponse } from '../common/types/api.types';
import { TCartItemType, TSearchRole } from '../common/types/index.types';
import CookieHelper from '../helpers/CookieHelper';
import { INewVendorVolumeData, ITopNewPartnerVolumePartner } from '@/common/interfaces/team.interface';

export default class ZApiService {
  public async isOrderSwitchActiveForRoute(apiRoute: ZApiRoutes<unknown, unknown, unknown>): Promise<boolean> {
    const data = await ZApiRoutes.getSwitchOrderRoutes.fetch();

    // Ignoring this TS error because this endpoint doesn't have the "data" attribute in the response as the TApiResponse suggests
    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    // @ts-ignore
    return data[apiRoute.getUrl()] && data[apiRoute.getUrl()].indexOf(apiRoute.getMethod().toUpperCase()) >= 0;
  }

  public async postBarometerData(): Promise<IApiBarometerData> {
    const response = await ZApiRoutes.postBarometerData.fetch();
    return response.data;
  }

  public async getCurrencies(): Promise<ICurrency[]> {
    const response = await ZApiRoutes.getCurrencies.fetch();
    return response.data ?? [];
  }

  public async getDashboardData(): Promise<IDashboardData> {
    const response = await ZApiRoutes.getDashboardData.fetch();
    return response.data;
  }

  public async getLoginCheck(): Promise<boolean> {
    const response = await ZApiRoutes.getLoginCheck.fetch();
    return response.success ?? false;
  }

  public async getCalendarData(data: IGetCalendarRequestData): Promise<ICalendarData> {
    const response = await ZApiRoutes.getCalendarData.fetch({
      data,
    });
    return response.data;
  }

  public async getPartnerInfo(name: string): Promise<TApiResponse<IApiPartnerInfo>> {
    const response = await ZApiRoutes.getPartnerInfo.fetch({
      params: {
        name,
      },
    });
    return {
      data: response.data,
      success: response.success ?? false,
    };
  }

  public async getProductInfos(matchcode: string): Promise<IProduct> {
    const response = await ZApiRoutes.getProductByMatchcode.fetch({
      params: {
        matchcode,
      },
    });
    return response.data;
  }

  public async getReducedProducts(currency?: ECurrency, language?: ELanguage): Promise<IProduct[]> {
    const response = await ZApiRoutes.getReducedProducts.fetch({
      data: {
        currency: currency || CookieHelper.getCurrency(),
        language: language || CookieHelper.getLanguage(),
      },
    });
    return response.data;
  }

  public async getSalutations(): Promise<ISalutation[]> {
    const response = await ZApiRoutes.getSalutations.fetch();
    return response.data;
  }

  public async patchNewAddress(addressData: IAddress | IUpdateAddress): Promise<IAddress> {
    const response = await ZApiRoutes.patchNewAddress.fetch({
      data: addressData,
    });

    return {
      city: response.data.city,
      country: response.data.country,
      countryISO: response.data.countryISO,
      countryId: response.data.countryId,
      deliveryDefault: response.data.deliveryDefault,
      extra: response.data.extra,
      id: response.data.id,
      receiptDefault: response.data.receiptDefault,
      recipient: response.data.recipient,
      street: response.data.street,
      streetNumber: response.data.streetNumber,
      zipCode: response.data.zipCode,
    };
  }

  public async updateNewsletter(newsletterData: IApiNewsletterFormData): Promise<boolean> {
    const response = await ZApiRoutes.updateNewsletter.fetch({
      data: newsletterData,
    });
    return response.success ?? false;
  }

  public async unsubscribeNewsletter(email: string, salutationId: ESalutation): Promise<boolean> {
    const response = await ZApiRoutes.unsubscribeNewsletter.fetch({
      data: {
        email,
        salutationId,
      },
    });
    return response.success ?? false;
  }

  public async postNewsletterConfirm(action: string, checksum: string, email: string): Promise<void> {
    await ZApiRoutes.postNewsletterConfirm.fetch({
      data: {
        action,
        checksum,
        email,
      },
    });
  }

  public async registerForGiveaway(data: INewsletterFormData, calendarId: number, doorId: number): Promise<boolean | string> {
    const registrationFormData = AnnouncementBarHelper.transformGiveawayFormDataToApiGiveawayRegistrationFormData(data, calendarId, doorId);
    try {
      const response = await ZApiRoutes.registerForGiveawayDoor.fetch({
        data: registrationFormData,
      });
      return response.success ?? false;
    } catch (err) {
      return err.message;
    }
  }

  public async getSearchData(
    lang: ELanguage,
    limit: number,
    showHiddenCategories: boolean,
    term: string,
    role: TSearchRole,
  ): Promise<TApiResponse<IApiSearchResult[]>> {
    return ZApiRoutes.getSearchData.fetch({
      data: {
        lang,
        limit,
        showHiddenCategories,
        term,
      },
      params: {
        role,
      },
    });
  }

  public async addItemToCart(data: IApiAddCartItemRequestBody, cartId?: number): Promise<IShoppingCart> {
    let ordersSwitchActive = false;
    if(!data.isPartnerApplication) {
      ordersSwitchActive = await this.isOrderSwitchActiveForRoute(ZApiRoutes.postItemToCart);
    }
    const response = await ZApiRoutes.postItemToCart.fetch({
      data: {
        amount: data.amount ?? 1,
        cartId,
        couponAmount: data.couponAmount,
        couponCurrency: data.couponCurrency,
        couponEmailFrom: data.couponEmailFrom,
        couponEmailTo: data.couponEmailTo,
        couponFrom: data.couponFrom,
        couponId: data.couponId,
        couponMedium: data.couponMedium,
        couponTo: data.couponTo,
        customText1: data.customText1,
        customText2: data.customText2,
        isPartnerApplication: data.isPartnerApplication ?? false,
        matchcode: data.matchcode,
        matchcodeBlocked: data.matchcodeBlocked,
        setSwap: data.setSwap ?? false,
      },
      ordersSwitchActive,
    });
    return response.data;
  }

  public async updateProductVariant(
    amount: number,
    cartContentId: number,
    cartId: number,
    matchcode: string,
    session?: string,
    isPartnerApplication = false,
    setSwap = false,
  ): Promise<IShoppingCart> {
    let ordersSwitchActive = false;
    if(!isPartnerApplication) {
      ordersSwitchActive = await this.isOrderSwitchActiveForRoute(ZApiRoutes.updateProductVariant);
    }
    const response = await ZApiRoutes.updateProductVariant.fetch({
      data: {
        amount,
        cartContentId,
        cartId,
        isPartnerApplication,
        matchcode,
        session,
        setSwap,
      },
      ordersSwitchActive,
    });
    return response.data;
  }

  public async updateCartItem(data: IPatchCartItemRequestData, isPartnerApplication = false): Promise<IShoppingCart> {
    let ordersSwitchActive = false;
    if(!isPartnerApplication) {
      ordersSwitchActive = await this.isOrderSwitchActiveForRoute(ZApiRoutes.patchCartItem);
    }
    const response = await ZApiRoutes.patchCartItem.fetch({
      data,
      ordersSwitchActive,
    });
    return response.data;
  }

  public async removeItemFromCart(cartContentId: number, cartId: number, isPartnerApplication = false, type?: TCartItemType): Promise<IShoppingCart> {
    let ordersSwitchActive = false;
    if(!isPartnerApplication) {
      ordersSwitchActive = await this.isOrderSwitchActiveForRoute(ZApiRoutes.deleteItemFromCart);
    }
    const response = await ZApiRoutes.deleteItemFromCart.fetch({
      data: {
        cartContentId,
        cartId,
        isPartnerApplication,
        type,
      },
      ordersSwitchActive,
    });
    return response.data;
  }

  public async getPartnerApplication(addressId: number): Promise<IPartnerApplication> {
    const response = await ZApiRoutes.getPartnerApplication.fetch({
      params: {
        addressId: addressId.toString(),
      },
    });
    return response.data;
  }

  public async getAddresses(customerNumber?: number): Promise<IAddress[]> {
    const response = await ZApiRoutes.getAddresses.fetch({
      data: {
        customerNumber,
      },
    });
    return response.data;
  }

  public async getAddressById(addressId: number): Promise<IAddress> {
    const response = await ZApiRoutes.getAddressById.fetch({
      params: {
        addressId,
      },
    });
    return response.data[0];
  }

  public async getCountries(showAllCountries = false): Promise<ICountry[]> {
    const response = showAllCountries ? await ZApiRoutes.getAllCountries.fetch() : await ZApiRoutes.getCountries.fetch();
    return response.data;
  }

  public async postPartnerSearch(searchString: string): Promise<IPartnerSearchResult[]> {
    const response = await ZApiRoutes.postPartnerSearch.fetch({
      data: {
        searchString,
      },
    });
    return response.data;
  }

  public async postPartnerApplication(sendPartnerApplicationDto: IPartnerApplicationSendPADto): Promise<IPartnerApplicationResponse> {
    const response = await ZApiRoutes.postPartnerApplication.fetch({
      data: sendPartnerApplicationDto,
    });
    return response.data;
  }

  public async postPartnerApplicationPreCheck(sendPartnerApplicationDto: IPartnerApplicationSendPADto): Promise<boolean> {
    const response = await ZApiRoutes.postPartnerApplicationPreCheck.fetch({
      data: sendPartnerApplicationDto,
    });
    return response.success ?? false;
  }

  public async getPartnerApplicationProducts(): Promise<IProduct[]> {
    const response = await ZApiRoutes.getPartnerApplicationProducts.fetch();
    return response.data;
  }

  public async postPartnerImage(image: IImage): Promise<string> {
    const data = new FormData();
    data.append(`file`, image.imageUrl as File);

    const response = await ZApiRoutes.postPartnerImage.fetch({
      data,
      formData: true,
    });
    return response.data;
  }

  public async getCarts(id: number | string): Promise<IShoppingCart> {
    const response = await ZApiRoutes.getCart.fetch({
      params: {
        id,
      },
    });
    return response.data;
  }

  public async postCartCreate(type: ECartTypeId = ECartTypeId.PERSONAL_USE, isPartnerApplication = false, customerNumber?: number): Promise<ICreateShoppingCart & IShoppingCart> {
    let ordersSwitchActive = false;
    if(!isPartnerApplication) {
      ordersSwitchActive = await this.isOrderSwitchActiveForRoute(ZApiRoutes.postCartCreate);
    }
    const response = await ZApiRoutes.postCartCreate.fetch({
      data: {
        customerNumber,
        type,
      },
      ordersSwitchActive,
    });
    return response.data;
  }

  public async deleteCart(cartId: number, isPartnerApplication = false): Promise<boolean> {
    let ordersSwitchActive = false;
    if(!isPartnerApplication) {
      ordersSwitchActive = await this.isOrderSwitchActiveForRoute(ZApiRoutes.deleteCart);
    }
    const response = await ZApiRoutes.deleteCart.fetch({
      data: {
        cartId,
      },
      ordersSwitchActive,
    });
    return response.success || false;
  }

  public async getCartCount(): Promise<number> {
    let amount;
    try {
      const ordersSwitchActive = await this.isOrderSwitchActiveForRoute(ZApiRoutes.getCartCount);
      const response = await ZApiRoutes.getCartCount.fetch({ ordersSwitchActive });
      amount = response.amount ?? 0;
    } catch (err) {
      amount = 0;
    }
    return amount;
  }

  public async verifyTaxNumber(data: ICheckoutTaxNumberData): Promise<boolean> {
    const response = await ZApiRoutes.verifyTaxNumber.fetch({
      data,
    });
    return response.success || false;
  }

  public async getIncentives(): Promise<IIncentive[]> {
    const response = await ZApiRoutes.getIncentives.fetch();
    return response.data;
  }

  public async getIncentiveRanking(id: number, limit: number): Promise<IIncentiveRanking> {
    const response = await ZApiRoutes.getIncentiveRanking.fetch({
      data: {
        limit,
      },
      params: {
        id,
      },
    });
    return {
      ranking: response.data.ranking,
      rankingCount: response.data.rankingCount as number,
    };
  }

  public async getIncentiveClimberRanking(id: number, limit: number): Promise<IIncentiveRanking> {
    const response = await ZApiRoutes.getIncentiveRanking.fetch({
      data: {
        limit,
      },
      params: {
        id,
      },
    });
    return {
      climber: response.data.climber,
      climberCount: response.data.climberCount as number,
    };
  }

  public async getIncentiveData(id: number): Promise<IIncentivesData> {
    const response = await ZApiRoutes.getIncentiveData.fetch({
      params: {
        id,
      },
    });
    return response.data;
  }

  public async getMasterSummitDashboardData(id: number): Promise<IMasterSummitDashboardData> {
    const response = await ZApiRoutes.getMasterSummitDashboardData.fetch({
      params: {
        id,
      },
    });
    return response.data;
  }

  public async getSpecialSummitDashboardData(id: number): Promise<ISpecialSummitDashboardData> {
    const response = await ZApiRoutes.getSpecialSummitDashboardData.fetch({
      params: {
        id,
      },
    });
    return response.data;
  }

  public async getSearchResults(id: number, searchValue: string): Promise<ISearchResults[]> {
    const response = await ZApiRoutes.getSearchResults.fetch({
      params: {
        id,
        search: searchValue,
      },
    });
    return response.data;
  }

  public async votePartner(cn: number, id: number): Promise<IVotePartner> {
    const response = await ZApiRoutes.votePartner.fetch({
      params: {
        cn,
        id,
      },
    });
    return response.data;
  }

  public async redeemCoupon(cartId: number, code: string, isPartnerApplication = false): Promise<IShoppingCart> {
    let ordersSwitchActive = false;
    if(!isPartnerApplication) {
      ordersSwitchActive = await this.isOrderSwitchActiveForRoute(ZApiRoutes.redeemCoupon);
    }
    const response = await ZApiRoutes.redeemCoupon.fetch({
      data: {
        cartId,
        code,
        isPartnerApplication,
      },
      ordersSwitchActive,
    });
    return response.data;
  }

  public async removeCoupon(cartContentId: number, cartId: number, code: string, isPartnerApplication = false): Promise<IShoppingCart> {
    let ordersSwitchActive = false;
    if(!isPartnerApplication) {
      ordersSwitchActive = await this.isOrderSwitchActiveForRoute(ZApiRoutes.removeCoupon);
    }
    const response = await ZApiRoutes.removeCoupon.fetch({
      data: {
        cartContentId,
        cartId,
        code,
        isPartnerApplication,
      },
      ordersSwitchActive,
    });
    return response.data;
  }

  public async getDocuSignRecipientViewData(
    options: IItalianPartnerApplicationDocumentOptions | IFrenchPartnerApplicationDocumentOptions | IGenericPartnerApplicationDocumentOptions,
  ): Promise<IDocuSignRecipientViewData> {
    const response = await ZApiRoutes.createDocuSignRecipientView.fetch({
      data: options,
    });
    return response.data;
  }

  public async postItalianPartnerPicture(image: IImage): Promise<IImage> {
    const response = await ZApiRoutes.postItalianPartnerPicture.fetch({
      data: image,
    });
    return response.data;
  }

  public async updateItalianPartnerPicture(image: IImage): Promise<IImage> {
    const response = await ZApiRoutes.updateItalianPartnerPicture.fetch({
      data: image,
    });
    return response.data;
  }

  public async updateQuantity(amount: number, cartContentId: number, isPartnerApplication: boolean, matchcode: string): Promise<IShoppingCart> {
    let ordersSwitchActive = false;
    if(!isPartnerApplication) {
      ordersSwitchActive = await this.isOrderSwitchActiveForRoute(ZApiRoutes.patchCartItem);
    }
    const response = await ZApiRoutes.patchCartItem.fetch({
      data: {
        amount,
        cartContentId,
        matchcode,
      },
      ordersSwitchActive,
    });

    return response.data;
  }

  public async getItalianPartnerPicture(): Promise<IImage> {
    const response = await ZApiRoutes.getItalianPartnerPicture.fetch();
    return response.data;
  }

  public async postImage(image: File): Promise<IImage> {
    const imageFormData = new FormData();
    imageFormData.append(`file`, image);

    const response = await ZApiRoutes.postImage.fetch({
      data: imageFormData,
      formData: true,
    });
    return response.data;
  }

  public async deleteImage(key: string): Promise<void> {
    await ZApiRoutes.deleteImage.fetch({
      params: {
        key,
      },
    });
  }

  public async getBlogPosts(blogPostIds: number[], limit: number, lang: ELanguage): Promise<IBlogPostsData[]> {
    const response = await ZApiRoutes.getBlogPosts.fetch({
      data: {
        ids: blogPostIds,
        lang,
        limit,
      },
    });
    return response.data;
  }

  public async getCustomerSession(): Promise<ICustomerSession> {
    const response = await ZApiRoutes.getCustomerSession.fetch();
    return response.data;
  }

  public async getChannelizeBroadcasts(): Promise<IChannelizeBroadcast[]> {
    const response = await ZApiRoutes.getBroadcasts.fetch();
    return response.data;
  }

  public async getEvents(): Promise<IEvent[]> {
    const response = await ZApiRoutes.getEvents.fetch();
    return EventsHelper.transformApiEventsToEvents(response.data);
  }

  public async getCustomerDatasheet(customerNumber: number): Promise<IApiCustomerDataSheet> {
    const response = await ZApiRoutes.getCustomerDatasheet.fetch({
      data: {
        customerNumber,
      },
    });
    return response.data;
  }

  public async transferCustomer(customerNumber: number, partner: number): Promise<void> {
    await ZApiRoutes.transferCustomer.fetch({
      data: {
        customerNumber,
        partner,
      },
    });
  }

  public async suggestAsPartner(customerNumber: number): Promise<void> {
    await ZApiRoutes.postVendorRecommendation.fetch({
      data: {
        customerNumber,
      },
    });
  }

  public async getFairScore(customerNumber: number): Promise<number> {
    const response = await ZApiRoutes.getFairScore.fetch({
      data: {
        customerNumber,
      },
    });
    return response.data;
  }

  public async getUpline(customerNumber?: number): Promise<IUplineData[]> {
    const data = customerNumber ? { customerNumber } : undefined;
    const response = await ZApiRoutes.getUpline.fetch({
      data,
    });
    return response.data;
  }

  public async getCoupons(customerNumber: number, genericAutoredeem = false): Promise<IApiCoupon[]> {
    const response = await ZApiRoutes.getCoupons.fetch({
      data: {
        customerNumber,
        genericAutoredeem,
      },
    });

    return response.data;
  }

  public async getDatasheetOrders(filter: IApiDatasheetOrdersFilter, offset: number, limit: number): Promise<{ orders: IApiDataSheetOrder[]; totalOrders: number }> {
    const response = await ZApiRoutes.getDatasheetOrders.fetch({
      data: {
        ...filter,
        limit,
        offset,
      },
    });
    return {
      orders: response.data,
      totalOrders: response.count ?? 0,
    };
  }

  public async getCustomer(customerNumber?: number): Promise<IVoucherAssignmentCustomerData> {
    const response = await ZApiRoutes.getCustomer.fetch({
      data: {
        customerNumber: customerNumber as number,
      },
    });
    return {
      ...response.data.account,
      name: `${response.data.personalInfo.firstName} ${response.data.personalInfo.lastName}`,
    };
  }

  public async sendVoucherAssignmentMail(voucherCode: string): Promise<void> {
    await ZApiRoutes.sendVoucherAssignmentMail.fetch({
      data: {
        voucherCode,
      },
    });
  }

  public async getCoordinates(mapParams: IMapParams): Promise<ICoordinates> {
    const response = await ZApiRoutes.getCoordinates.fetch({
      data: mapParams,
    });
    return response.data;
  }

  public async addSupplement(cartId: number, supplementId: number, isPartnerApplication = false): Promise<IShoppingCart> {
    let ordersSwitchActive = false;
    if(!isPartnerApplication) {
      ordersSwitchActive = await this.isOrderSwitchActiveForRoute(ZApiRoutes.addSupplement);
    }
    const response = await ZApiRoutes.addSupplement.fetch({
      data: {
        cartId,
        isPartnerApplication,
        supplementId,
      },
      ordersSwitchActive,
    });
    return response.data;
  }

  public async getBouncerConfig(): Promise<IBouncerConfig> {
    const response = await ZApiRoutes.getBouncerConfig.fetch();
    return response.data;
  }

  public async checkoutAddresses(cartId: number | string, partnerShop?: string): Promise<IAddressData> {
    const response = await ZApiRoutes.checkoutAddresses.fetch({
      data: {
        cartId,
        partnerShop,
      },
    });
    return response.data;
  }

  public async getNewVendorVolumeData(invoicingId?: number): Promise<INewVendorVolumeData> {
    const response = await ZApiRoutes.getNewVendorVolumes.fetch({
      data: {
        invoicingId,
      },
    });
    return response.data;
  }

  public async getTopNewPartnerVolumePartners(amount?: number, invoicingId?: number): Promise<ITopNewPartnerVolumePartner[]> {
    const response = await ZApiRoutes.getTopNewPartnerVolumePartners.fetch({
      data: {
        amount,
        invoicingId,
      },
    });
    return response.data;
  }
}
